<script setup lang="ts">
import type { ClinicResponse, NearestClinicResponse } from '@/types'
import { isCurrentlyOpen } from '@/types'
import { pluralCz } from '~/composables/useSeoHelpers'

const route = useRoute()
const router = useRouter()

const searchQuery = computed(() => (route.query.q as string) || '')
const geoLat = computed(() => route.query.lat as string | undefined)
const geoLon = computed(() => route.query.lon as string | undefined)
const geoOpenNow = computed(() => route.query.openNow === 'true')
const countySlugParam = computed(() => route.query.county as string | undefined)
const citySlugParam = computed(() => route.query.citySlug as string | undefined)
const radiusKmParam = computed(() => route.query.radiusKm ? Number(route.query.radiusKm) : undefined)
const page = computed(() => Number(route.query.page) || 1)
const limit = 20

const activeRadiusKm = ref(radiusKmParam.value)

const { activeFilter, setFilter } = useClinicFilter()

const offset = computed(() => (page.value - 1) * limit)

const isCityRadiusMode = computed(() => !!citySlugParam.value && !!radiusKmParam.value)
const isCityMode = computed(() => !!citySlugParam.value && !radiusKmParam.value)
const isSearchMode = computed(() => !isCityRadiusMode.value && !isCityMode.value && searchQuery.value.length >= 2)
const isGeoMode = computed(() => !isCityRadiusMode.value && !isCityMode.value && !!geoLat.value && !!geoLon.value)

// Mode 1: Default / city clinics listing
const clinicsOptions = computed(() => ({
  countySlug: countySlugParam.value,
  citySlug: citySlugParam.value,
  isEmergency: activeFilter.value === 'emergency' || undefined,
  is24Hours: activeFilter.value === 'nonstop' || undefined,
  openNow: activeFilter.value === 'open' || undefined,
  limit,
  offset: offset.value,
}))
const { data: clinicsData, pending: clinicsPending } = await useClinics(clinicsOptions)

// Mode 2: Text search
interface SearchCityResult {
  id: string
  name: string
  slug: string
  county: { name: string; slug: string }
  clinicCount: number
}
interface SearchResponse {
  clinics: ClinicResponse[]
  total: number
  cities: SearchCityResult[]
}

const searchKey = computed(() => `search-${searchQuery.value}-${page.value}`)
const { data: searchData, pending: searchPending } = await useAsyncData(
  searchKey,
  () => $fetch<{ success: boolean; data: SearchResponse }>('/api/search', {
    query: { q: searchQuery.value, limit, offset: offset.value },
  }),
  { watch: [searchQuery, page], immediate: !!searchQuery.value },
)

const searchCities = computed(() => searchData.value?.data?.cities?.filter(c => c.clinicCount > 0) ?? [])

// Mode 3: Geolocation nearest
const nearestKey = computed(() => `nearest-${geoLat.value}-${geoLon.value}-${geoOpenNow.value}`)
const { data: nearestData, pending: nearestPending } = await useAsyncData(
  nearestKey,
  () => {
    if (!geoLat.value || !geoLon.value) {
      return Promise.resolve({ success: true as const, data: [] as NearestClinicResponse[] })
    }
    return $fetch<{ success: boolean; data: NearestClinicResponse[] }>('/api/clinics/nearest', {
      query: {
        lat: geoLat.value,
        lon: geoLon.value,
        openNow: geoOpenNow.value || undefined,
      },
    })
  },
  { watch: [geoLat, geoLon, geoOpenNow] },
)

// Mode 4: City nearest (citySlug + radiusKm)
const citySlugKey = computed(() => `city-nearest-${citySlugParam.value}-${radiusKmParam.value}`)
const { data: cityNearestData, pending: cityNearestPending } = await useAsyncData(
  citySlugKey,
  () => {
    if (!citySlugParam.value || !radiusKmParam.value) {
      return Promise.resolve({ success: true as const, data: [] as NearestClinicResponse[] })
    }
    return $fetch<{ success: boolean; data: NearestClinicResponse[] }>('/api/clinics/nearest', {
      query: {
        citySlug: citySlugParam.value,
        radiusKm: radiusKmParam.value,
      },
    })
  },
  { watch: [citySlugParam, radiusKmParam] },
)

const cityName = computed(() => {
  if (isCityRadiusMode.value) {
    return cityNearestData.value?.data?.[0]?.nearestTo || citySlugParam.value
  }
  // City-only mode: get name from first clinic's city
  const firstClinic = clinicsData.value?.data?.[0]
  return firstClinic?.city?.name || citySlugParam.value
})

const pending = computed(() => {
  if (isCityRadiusMode.value) return cityNearestPending.value
  if (isGeoMode.value) return nearestPending.value
  if (isSearchMode.value) return searchPending.value
  return clinicsPending.value
})

function filterByActiveMode(results: (ClinicResponse | NearestClinicResponse)[]): (ClinicResponse | NearestClinicResponse)[] {
  if (activeFilter.value === 'emergency') {
    return results.filter(c => c.isEmergency)
  }
  if (activeFilter.value === 'nonstop') {
    return results.filter(c => c.isEmergency && c.is24Hours)
  }
  if (activeFilter.value === 'open') {
    return results.filter((c) => {
      if ((c.is24Hours && c.isEmergency) || c.isOnCallOnly) return true
      if (isCurrentlyOpen(c.workingHours)) return true
      return isCurrentlyOpen(c.emergencyHours) === true
    })
  }
  return results
}

const clinics = computed(() => {
  if (isCityRadiusMode.value) return filterByActiveMode(cityNearestData.value?.data || [])
  if (isGeoMode.value) return filterByActiveMode(nearestData.value?.data || [])
  if (isSearchMode.value) return searchData.value?.data?.clinics || []
  return clinicsData.value?.data || []
})

const total = computed(() => {
  if (isSearchMode.value) return searchData.value?.data?.total || 0
  if (isCityRadiusMode.value || isGeoMode.value) return clinics.value.length
  return clinicsData.value?.total || 0
})
const totalPages = computed(() => (isCityRadiusMode.value || isGeoMode.value) ? 1 : Math.ceil(total.value / limit))

// Counts for subtitle (unfiltered scope counts)
function getRawResults(): (ClinicResponse | NearestClinicResponse)[] {
  if (isCityRadiusMode.value) return cityNearestData.value?.data || []
  if (isGeoMode.value) return nearestData.value?.data || []
  return []
}
const emergencyCount = computed(() => {
  if (isSearchMode.value) return 0
  if (!isCityRadiusMode.value && !isGeoMode.value) {
    return clinicsData.value?.emergencyTotal ?? 0
  }
  return getRawResults().filter(c => c.isEmergency).length
})
const nonstopCount = computed(() => {
  if (isSearchMode.value) return 0
  if (!isCityRadiusMode.value && !isGeoMode.value) {
    return clinicsData.value?.nonstopTotal ?? 0
  }
  return getRawResults().filter(c => c.isEmergency && c.is24Hours).length
})

// Sync URL → activeRadiusKm when route changes (e.g. navigating from city page)
watch(radiusKmParam, (val) => {
  if (val !== activeRadiusKm.value) {
    activeRadiusKm.value = val
  }
})

// Sync activeRadiusKm → URL when user toggles radius buttons
watch(
  () => activeRadiusKm.value,
  (radiusKm) => {
    if (radiusKm === radiusKmParam.value) return
    router.push({
      query: {
        ...route.query,
        radiusKm: radiusKm || undefined,
        page: undefined,
      },
    })
  },
)

function onPageChange(newPage: number) {
  router.push({
    query: {
      ...route.query,
      page: newPage > 1 ? newPage : undefined,
    },
  })
}

const geoLoading = ref(false)
const geoError = ref<string>()

function requestGeolocation() {
  if (!navigator.geolocation) {
    geoError.value = 'Váš prohlížeč nepodporuje geolokaci.'
    return
  }
  geoLoading.value = true
  geoError.value = undefined
  navigator.geolocation.getCurrentPosition(
    (position) => {
      geoLoading.value = false
      router.push({
        path: '/veteriny',
        query: {
          lat: position.coords.latitude.toFixed(6),
          lon: position.coords.longitude.toFixed(6),
          openNow: 'true',
        },
      })
    },
    () => {
      geoLoading.value = false
      geoError.value = 'Nepodařilo se zjistit vaši polohu. Povolte přístup k poloze v prohlížeči.'
    },
  )
}

useSeoMeta({
  title: 'Veterinární kliniky – Seznam všech veterinářů',
  description: `Kompletní seznam veterinárních klinik v České republice. Najděte ${total.value} klinik s kontakty, hodnocením a pohotovostními službami.`,
  twitterCard: 'summary_large_image',
})

defineOgImage({
  props: {
    title: 'Veterinární kliniky v ČR',
    description: 'Kompletní seznam všech veterinářů',
  },
})

const breadcrumbs = [
  { label: 'Domů', to: '/' },
  { label: 'Veteriny' },
]
</script>

<template>
  <div class="container mx-auto px-4 py-8">
    <UBreadcrumb :items="breadcrumbs" class="mb-6" />

    <div class="mb-6">
      <div class="flex flex-wrap items-center gap-3">
        <h1 class="text-2xl md:text-3xl font-bold text-highlighted">
          <template v-if="isCityRadiusMode">
            Kliniky v okolí {{ cityName }}
          </template>
          <template v-else-if="isCityMode">
            Veterináři v {{ cityName }}
          </template>
          <template v-else-if="isGeoMode">
            Kliniky ve vašem okolí
          </template>
          <template v-else-if="isSearchMode">
            Výsledky pro „{{ searchQuery }}"
          </template>
          <template v-else>
            Veterinární kliniky
          </template>
        </h1>
        <ClientOnly>
          <UButton
            v-if="!isGeoMode && !isCityRadiusMode && !isCityMode"
            size="sm"
            color="primary"
            variant="soft"
            icon="i-lucide-locate"
            :loading="geoLoading"
            @click="requestGeolocation"
          >
            Hledat v mém okolí
          </UButton>
        </ClientOnly>
      </div>
      <p v-if="geoError" class="text-sm text-red-500 mt-1">{{ geoError }}</p>
      <p class="text-muted mt-1">
        {{ total }} {{ pluralCz(total, 'klinika', 'kliniky', 'klinik') }}
        <button
          v-if="emergencyCount > 0"
          class="inline-flex items-center gap-1 ml-2 text-red-600 hover:underline cursor-pointer"
          @click="setFilter('emergency')"
        >
          <UIcon name="i-lucide-siren" class="w-3.5 h-3.5" />
          {{ emergencyCount }} {{ pluralCz(emergencyCount, 'pohotovost', 'pohotovosti', 'pohotovostí') }}
        </button>
        <button
          v-if="nonstopCount > 0"
          class="inline-flex items-center gap-1 ml-2 text-blue-600 hover:underline cursor-pointer"
          @click="setFilter('nonstop')"
        >
          <UIcon name="i-lucide-clock" class="w-3.5 h-3.5" />
          {{ nonstopCount }} nonstop
        </button>
      </p>
    </div>

    <FilterBar
      class="mb-8"
      :radius-options="(isCityRadiusMode || isCityMode || isGeoMode) ? [{ km: 10 }, { km: 25 }] : undefined"
      :active-radius-km="activeRadiusKm"
      @update:active-radius-km="activeRadiusKm = $event"
    />

    <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <USkeleton v-for="i in 6" :key="i" class="h-32" />
    </div>

    <div v-else-if="clinics.length || searchCities.length" class="space-y-8">
      <!-- City matches banner -->
      <div v-if="isSearchMode && searchCities.length" class="flex flex-wrap gap-3">
        <div
          v-for="city in searchCities"
          :key="city.id"
          class="flex items-center gap-3 px-4 py-3 rounded-lg border border-primary/20 bg-primary-50/50 dark:bg-primary-950/40 transition-colors"
        >
          <UIcon name="i-lucide-map-pin" class="size-5 text-primary shrink-0" />
          <div>
            <NuxtLink
              :to="`/${city.county.slug}/${city.slug}`"
              class="font-medium text-highlighted hover:underline"
            >
              {{ city.name }}
            </NuxtLink>
            <span class="text-sm text-muted ml-1">({{ city.county.name }})</span>
            <p class="text-sm text-primary">{{ city.clinicCount }} {{ pluralCz(city.clinicCount, 'klinika', 'kliniky', 'klinik') }}</p>
          </div>
          <div class="flex items-center gap-1.5 ml-auto shrink-0">
            <NuxtLink
              :to="{ path: '/veteriny', query: { citySlug: city.slug, radiusKm: 10 } }"
              class="text-xs px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              +10 km
            </NuxtLink>
            <NuxtLink
              :to="{ path: '/veteriny', query: { citySlug: city.slug, radiusKm: 25 } }"
              class="text-xs px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              +25 km
            </NuxtLink>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ClinicCard
          v-for="clinic in clinics"
          :key="clinic.id"
          :clinic="clinic"
          :show-distance="isGeoMode || isCityRadiusMode"
        />
      </div>

      <div v-if="totalPages > 1" class="flex justify-center">
        <UPagination
          :page="page"
          :total="total"
          :items-per-page="limit"
          @update:page="onPageChange"
        />
      </div>
    </div>

    <EmptyState
      v-else
      icon="i-lucide-search-x"
      title="Žádné výsledky"
      :description="activeFilter !== 'all' ? 'Zkuste zobrazit všechny kliniky.' : 'Zkuste změnit kritéria vyhledávání.'"
    >
      <UButton
        v-if="activeFilter !== 'all'"
        color="primary"
        @click="setFilter('all')"
      >
        Zobrazit všechny kliniky
      </UButton>
    </EmptyState>
  </div>
</template>
